/**
 * API client for the admin SPA.
 *
 * Talks to:
 *   - `/auth/*`      (public — login, refresh)
 *   - `/admin/api/*` (auth+admin gated)
 *
 * Both originate at the same domain in prod (`taiya.omni.tel`)
 * because Caddy reverse-proxies `/admin/api/*` and `/auth/*` to the
 * api container. In dev, Vite proxies them to localhost:3000.
 *
 * Token persistence:
 *   - access + refresh tokens live in `localStorage` (admin-only
 *     device, low risk vs httpOnly cookies; cookies would require
 *     a CORS dance with the api subdomain).
 *   - On any 401 from `/admin/api/*`, we try a refresh once before
 *     giving up. If refresh fails, we clear tokens and the auth
 *     gate re-renders the login screen.
 */

const TOKEN_KEY = 'taiya_admin_access_token';
const REFRESH_KEY = 'taiya_admin_refresh_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access: string, refresh?: string): void {
  localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

let refreshInFlight: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  refreshInFlight = (async () => {
    try {
      const r = await fetch('/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!r.ok) return false;
      const j = await r.json();
      const data = j.data;
      if (!data?.accessToken) return false;
      setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown; isRetry?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const tok = getAccessToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !opts.isRetry && !path.startsWith('/auth/')) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(path, { ...opts, isRetry: true });
    clearTokens();
    // Trigger an auth-gate re-render. Listened by App's useEffect.
    window.dispatchEvent(new Event('taiya:auth-cleared'));
  }

  if (!res.ok) {
    const j = await res.json().catch(() => null);
    const code = j?.error?.code ?? 'UNKNOWN';
    const message = j?.error?.message ?? res.statusText ?? 'Request failed';
    throw new ApiError(res.status, code, message);
  }

  const json = await res.json();
  return json.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ── Login ─────────────────────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<{
  user: { id: string; email: string; displayName: string };
  accessToken: string;
  refreshToken: string;
}> {
  // /auth/login is public — no Authorization header needed
  const res = await fetch('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      j?.error?.code ?? 'UNKNOWN',
      j?.error?.message ?? 'Login failed',
    );
  }
  const j = await res.json();
  return j.data;
}
